import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
const colourHeroPreloads = new Map();

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#warmColourHeroes();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #selectionVersion = 0;

  #warmColourHeroes() {
    if (this.closest('product-card, dialog')) return;
    const template = this.querySelector('template[data-colour-hero-preloads]');
    if (!(template instanceof HTMLTemplateElement)) return;
    const grid = this.querySelector('.media-gallery__grid .product-media__image');
    const slide = this.querySelector('slideshow-slide .product-media__image');
    const hero = grid?.getBoundingClientRect().width ? grid : slide;
    if (!hero) return;
    const warm = async () => {
      for (const image of template.content.querySelectorAll('img')) {
        if (!this.isConnected) return;
        const key = image.srcset + hero.sizes;
        if (colourHeroPreloads.has(key)) continue;
        const preload = new Image();
        preload.fetchPriority = 'low';
        preload.sizes = hero.sizes;
        preload.srcset = image.srcset;
        preload.src = image.src;
        colourHeroPreloads.set(key, preload);
        try {
          await preload.decode();
        } catch {
          colourHeroPreloads.delete(key);
        }
      }
    };
    hero.decode().catch(() => {}).then(() => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(warm, { timeout: 1500 });
      } else {
        window.setTimeout(warm, 200);
      }
    });
  }


  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const version = ++this.#selectionVersion;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const images = [
          newMediaGallery.querySelector('slideshow-slide .product-media__image'),
          newMediaGallery.querySelector('.media-gallery__grid .product-media__image'),
        ].filter(Boolean);
        if (!images.length) {
          const image = newMediaGallery.querySelector('.product-media__image');
          if (image) images.push(image);
        }
        await Promise.all(images.map((image) => {
          image.loading = 'eager';
          const preload = new Image();
          preload.sizes = image.sizes;
          preload.srcset = image.srcset;
          preload.src = image.src;
          return preload.decode();
        }));
        if (!this.isConnected || version !== this.#selectionVersion) return;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
